import React, { useEffect, useState } from "react";
import { IoDownloadOutline } from "react-icons/io5";
import { useNavigate, useSearchParams } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { motion } from "framer-motion";
import api from "../../utils/api";
import E_View from "./E_View";
import DownloadRequestModal from "./DownloadRequestModal";
import EmployeePayslipLoading from "../../utils/EmployeePayslipLoading";
import {
  IoPersonOutline, IoReceiptOutline
} from "react-icons/io5";

import { LuEye } from "react-icons/lu";

const E_Payslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [userId, setUserId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const payslipsPerPage = 6;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const searchTerm = (searchParams.get("search") || "").toLowerCase();

  // Ensure payslips is always an array
  const safePayslips = Array.isArray(payslips) ? payslips : [];

  // Filtered payslips based on search
  const filteredPayslips = safePayslips.filter((p) =>
    (p.salaryPeriod || "").toLowerCase().includes(searchTerm)
  );

  const totalPages = Math.ceil(filteredPayslips.length / payslipsPerPage);
  const paginatedPayslips = filteredPayslips.slice(
    (currentPage - 1) * payslipsPerPage,
    currentPage * payslipsPerPage
  );

  useEffect(() => {
    const fetchPayslips = async () => {
      try {
        setLoading(true);
        const authRes = await api.get(`/api/current-user`, {
          withCredentials: true,
        });
        const { id } = authRes.data;
        setUserId(id);

        const payslipRes = await api.get(`/userpayslip/${id}`);
        setPayslips(payslipRes.data);
        console.log("Payslips data:", payslipRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setUserId(0);
        setPayslips([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPayslips();
  }, []);

  const [openView, setOpenView] = useState(false);
  const [selectedPayId, setSelectedPayId] = useState(null);
  const [selectedSalaryPeriod, setSelectedSalaryPeriod] = useState(null);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [selectedPayForDownload, setSelectedPayForDownload] = useState(null);


  const handleRowClick = (payid, salaryPeriod) => {
    setSelectedPayId(payid);
    setSelectedSalaryPeriod(salaryPeriod);
    setOpenView(true);
  };

  const handleDownloadRequest = (e, payslip) => {
    e.stopPropagation(); // Prevent row click
    setSelectedPayForDownload(payslip);
    setOpenDownloadModal(true);
  };

    function formatSalaryPeriod(period) {
  if (!period) return "";

  const [year, month] = period.split("-");
  const date = new Date(year, month - 1);

  return date.toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
}


  const handleDownload = async (e, payid, salaryPeriod, employeeId) => {
    e.stopPropagation(); // Prevent row click
    try {
      const res = await api.get(`/payslip/download/${payid}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${employeeId}_payslip_${salaryPeriod}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF:", err);
    }
  };


  const handleDownloadRequestSuccess = (payid) => {
  setPayslips((prev) =>
    prev.map((p) =>
      p.payid === payid
        ? { ...p, status: "DOWNLOAD_REQUESTED" }
        : p
    )
  );
};


  return (
    <div className="sm:p-6 sm:mt-auto mt-8 am:mb-auto mb-20 w-full mx-auto">
      {/* Header */}
      <motion.div
        className="flex items-center gap-3 mb-6 bg-blue-100 rounded-xl p-4 sm:p-5 shadow-sm border border-slate-200"
        transition={{ duration: 0.4, type: "spring" }}>
         <div className='flex items-center gap-4 text-xl xs:text-2xl font-bold truncate'>
            <div className='p-2 rounded-xl bg-slate-700 text-slate-100'><IoReceiptOutline className="text-2xl" /></div>
            <h2 className="text-xl xs:text-2xl font-bold">My PaySlips</h2>
        </div>

      </motion.div>

      {/* Desktop Table */}
      <motion.div
        className="hidden sm:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        transition={{ duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">S.No</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Salary period</th>
                <th className="px-12 py-4 text-left text-sm font-semibold text-slate-600">Status</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-slate-600">Paid Days</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Download</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin"></div>
                    </div>
                  </td>
                </tr>
              ) : paginatedPayslips.length > 0 ? (
                paginatedPayslips.map((p, index) => (
                  <tr 
                    key={p.payid} 
                    onClick={() => handleRowClick(p.payid, p.salaryPeriod)}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-2 text-sm text-slate-800 font-medium">
                      {(currentPage - 1) * payslipsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-2 text-sm text-slate-800 font-medium">
                      {formatSalaryPeriod(p.salaryPeriod)}
                    </td>
                    <td className="px-6 py-2">
                      {p.status === 'DOWNLOAD_REQUESTED' ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">
                          ⏳ Download Requested
                        </span>
                      ) : p.status === 'APPROVED' ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                          APPROVED
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800">
                          PUBLISHED
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-2 text-sm text-slate-600">
                      {(() => {
                        const [monthStr, yearStr] = p.salaryPeriod.split("-");
                        const month = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
                        const year = parseInt(yearStr);
                        const totalDays = new Date(year, month + 1, 0).getDate();
                        return `${p.daysInMonth}/${totalDays}`;
                      })()}
                    </td>
                    <td className="px-6 py-2">


                      <motion.button
                        onClick={(e) => {
                          if (p.status === 'APPROVED') {
                            handleDownload(e, p.payid, p.salaryPeriod, p.employeeId);
                          } else {
                            handleDownloadRequest(e, p);
                          }
                        }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        title={p.status === 'APPROVED' ? 'Download' : 'Request Download'}
                      >
                        <IoDownloadOutline className={`text-xl ${
                          p.status === 'APPROVED'
                            ? 'text-slate-600'
                            : 'text-slate-400'
                        }`} />
                      </motion.button>

                    </td>
                    <td className="px-6 py-2">
                      <motion.button
  onClick={(e) => {
    e.stopPropagation();   // VERY IMPORTANT
    handleRowClick(p.payid, p.salaryPeriod);
  }}
  whileHover={{ scale: 1.1 }}
  whileTap={{ scale: 0.9 }}
  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
  title="View"
>
  <LuEye className="text-xl text-slate-600" />
</motion.button>
</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                    No payslips found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
            <Stack spacing={2} className="flex justify-end">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="rounded"
                size="small"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": { 
                    color: "#475569", 
                    borderColor: "#cbd5e1",
                    fontWeight: 500
                  },
                  "& .Mui-selected": { 
                    backgroundColor: "#475569 !important", 
                    color: "#fff",
                    borderColor: "#475569"
                  },
                }}
              />
            </Stack>
          </div>
        )}
      </motion.div>

      {/* Mobile Card */}
      <motion.div className="sm:hidden space-y-4" transition={{ duration: 0.5 }}>
        {loading ? (
          <EmployeePayslipLoading />
        ) : (
          paginatedPayslips.map((p) => (
            <motion.div
              key={p.payid}
              onClick={() => handleRowClick(p.payid, p.salaryPeriod)}
              className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 cursor-pointer"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-slate-800">
                    {p.salaryPeriod}
                  </span>
                      {p.status === 'DOWNLOAD_REQUESTED' ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">
                          ⏳ Download Requested
                        </span>
                      ) : p.status === 'APPROVED' ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                          APPROVED
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800">
                          PUBLISHED
                        </span>
                      )}
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Paid Days</div>
                    <div className="text-sm font-medium text-slate-700">
                      {(() => {
                        const [monthStr, yearStr] = p.salaryPeriod.split("-");
                        const month = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
                        const year = parseInt(yearStr);
                        const totalDays = new Date(year, month + 1, 0).getDate();
                        return `${p.daysInMonth}/${totalDays}`;
                      })()}
                    </div>
                  </div>
                  <motion.button
                    onClick={(e) => {
                      if (p.status === 'APPROVED') {
                        handleDownload(e, p.payid, p.salaryPeriod, p.employeeId);
                      } else {
                        handleDownloadRequest(e, p);
                      }
                    }}
                    className="p-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    
                  >
                    <IoDownloadOutline className="text-xl text-slate-600" />
                  </motion.button>
                  
                </div>
              </div>
            </motion.div>
          ))
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="rounded"
                size="medium"
                sx={{
                  "& .MuiPaginationItem-root": { 
                    color: "#475569", 
                    borderColor: "#cbd5e1" 
                  },
                  "& .Mui-selected": { 
                    backgroundColor: "#475569 !important", 
                    color: "#fff" 
                  },
                }}
              />
            </Stack>
          </div>
        )}
      </motion.div>

      {/* View Modal */}
      {openView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenView(false)}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: "0" }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 25 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col z-50 overflow-hidden"
          >
            {/* Header / Close Button */}
            <div className="flex justify-between items-center p-4 border-b border-slate-200 bg-white z-10">
              <h3 className="text-lg font-bold text-slate-700 pl-2"> Payslip for {formatSalaryPeriod(selectedSalaryPeriod)}</h3>
              <button
                onClick={() => setOpenView(false)}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors group"
                title="Close"
              >
                <svg
                  className="w-6 h-6 text-slate-400 group-hover:text-red-500 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content - Scrollable with Custom Scrollbar */}
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent p-4 sm:p-6">
              <E_View payid={selectedPayId} />
            </div>
          </motion.div>
        </div>
      )}

      {/* Download Request Modal */}
      {openDownloadModal && selectedPayForDownload && (
        <DownloadRequestModal
          payslip={selectedPayForDownload}
          userId={userId}
          onClose={() => {
            setOpenDownloadModal(false);
            setSelectedPayForDownload(null);
          }}
          onSuccess={handleDownloadRequestSuccess}
        />
      )}
    </div>
  );
};

export default E_Payslips;